import { useCallback, useMemo } from 'react'

import { PartnerFormMenuItems } from 'clickNgo/entities/partner/configs'
import { IPartner } from 'clickNgo/entities/partner/models'
import { IPartnerFormAdditional } from 'clickNgo/entities/partner/models/form/additional'
import { IPartnerFormBasic } from 'clickNgo/entities/partner/models/form/basic'
import {
  IPartnerFormKeys,
  IPartnerFormSettings,
} from 'clickNgo/entities/partner/models/form/common'
import { IPartnerFormLegal } from 'clickNgo/entities/partner/models/form/legal'
import { IAttachment } from 'clickNgo/models/file'
import { resetAllModifiedDataByKey, useGetFormData } from 'cng-common/features/form'
import { validateFormData } from 'cng-common/features/form/helpers/validateFormData'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'

interface IUsePartnerInfoFormData extends IPartnerFormKeys {
  partner?: IPartner
}

export const usePartnerInfoFormData = ({ formKeys }: IUsePartnerInfoFormData) => {
  const dispatch = useAppDispatch()
  const [
    {
      formData: basicInfoFormData,
      isValid: isBasicInfoFormDataValid,
      modifiedFields: basicInfoFormModifiedData,
    },
    updateBasicInfoFormData,
  ] = useGetFormData<IPartnerFormBasic>(formKeys.basic)
  const [
    {
      formData: legalInfoFormData,
      isValid: isLegalInfoFormDataValid,
      modifiedFields: legalInfoFormModifiedData,
    },
    updateLegalInfoFormData,
  ] = useGetFormData<IPartnerFormLegal>(formKeys.legal)
  const [
    {
      formData: additionalInfoFormData,
      isValid: isAdditionalInfoFormDataValid,
      modifiedFields: additionalInfoFormModifiedData,
    },
    updateAdditionalInfoFormData,
  ] = useGetFormData<IPartnerFormAdditional>(formKeys.additional)
  const [
    {
      formData: attachmentsFormData,
      isValid: isAttachmentsFormDataValid,
      modifiedFields: attachmentsFormModifiedData,
    },
    updateAttachmentsFormData,
  ] = useGetFormData<{ attachments: IAttachment[] }>(formKeys.attachments)

  const initPartnerInfoSetting = useMemo(
    () => ({
      currentMenuItem: PartnerFormMenuItems.Basic,
      doCopyBasicName: false,
      doCopyBasicAddress: false,
      doSearchPartnerDuplicates: true,
      doVisitPartnerAttachments: true,
      hasPartnerDuplicates: false,
      partnerDuplicates: [],
    }),
    []
  )

  const [{ formData: settingsFormData }, updateSettingsFormData] =
    useGetFormData<IPartnerFormSettings>(formKeys.settings, initPartnerInfoSetting)

  const hasBasicInfoFormModifiedData = Boolean(basicInfoFormModifiedData.length)
  const hasLegalInfoFormModifiedData = Boolean(legalInfoFormModifiedData.length)
  const hasAdditionalInfoFormModifiedData = Boolean(additionalInfoFormModifiedData.length)
  const hasAttachmentsFormModifiedData = Boolean(attachmentsFormModifiedData.length)

  const hasModifiedData =
    hasBasicInfoFormModifiedData ||
    hasLegalInfoFormModifiedData ||
    hasAdditionalInfoFormModifiedData ||
    hasAttachmentsFormModifiedData

  const isPartnerInfoFormDataValid =
    isBasicInfoFormDataValid &&
    isLegalInfoFormDataValid &&
    isAdditionalInfoFormDataValid &&
    isAttachmentsFormDataValid

  const partnerInfoFormDataCollection = useMemo(
    () => ({
      [formKeys.basic]: basicInfoFormData,
      [formKeys.legal]: legalInfoFormData,
      [formKeys.additional]: additionalInfoFormData,
      [formKeys.attachments]: attachmentsFormData,
    }),
    [
      additionalInfoFormData,
      attachmentsFormData,
      basicInfoFormData,
      formKeys.additional,
      formKeys.attachments,
      formKeys.basic,
      formKeys.legal,
      legalInfoFormData,
    ]
  )

  const handleOnPartnerDataValidate = useCallback(
    (formKey: string) => {
      switch (formKey) {
        case formKeys.basic: {
          updateSettingsFormData({
            field: 'currentMenuItem',
            value: PartnerFormMenuItems.Basic,
          })
          break
        }
        case formKeys.legal: {
          updateSettingsFormData({
            field: 'currentMenuItem',
            value: PartnerFormMenuItems.Legal,
          })
          break
        }
        case formKeys.additional: {
          updateSettingsFormData({
            field: 'currentMenuItem',
            value: PartnerFormMenuItems.Additional,
          })
          break
        }
        case formKeys.attachments: {
          updateSettingsFormData({
            field: 'currentMenuItem',
            value: PartnerFormMenuItems.Attachments,
          })
          break
        }
      }
    },
    [
      formKeys.additional,
      formKeys.attachments,
      formKeys.basic,
      formKeys.legal,
      updateSettingsFormData,
    ]
  )

  const resetPartnerInfoFormData = useCallback(() => {
    Object.values(formKeys).forEach((formKey) => {
      dispatch(resetAllModifiedDataByKey(formKey))
    })
  }, [dispatch, formKeys])

  const validatePartnerData = useCallback(async () => {
    return Object.entries(partnerInfoFormDataCollection).reduce(async (result, entry) => {
      const validationFormResult = await validateFormData(entry[1], entry[0])
      const hasValidationMessage = Boolean(validationFormResult.length)

      if (hasValidationMessage) {
        handleOnPartnerDataValidate(entry[0])
      }

      if (await result) {
        return !hasValidationMessage
      }

      return result
    }, Promise.resolve(true))
  }, [handleOnPartnerDataValidate, partnerInfoFormDataCollection])

  const formActualPartnerData = useCallback(
    (partner: IPartner) => {
      if (hasModifiedData) {
        const newModifiedData = [
          ...basicInfoFormModifiedData,
          ...legalInfoFormModifiedData,
          ...additionalInfoFormModifiedData,
        ]
        const prevModifiedData = partner.modifiedValues
        const modifiedValues = Array.from(new Set([...prevModifiedData, ...newModifiedData]))

        return {
          ...partner,
          ...basicInfoFormData,
          ...legalInfoFormData,
          ...additionalInfoFormData,
          ...attachmentsFormData,
          ...modifiedValues,
        }
      }

      return partner
    },
    [
      additionalInfoFormData,
      additionalInfoFormModifiedData,
      attachmentsFormData,
      basicInfoFormData,
      basicInfoFormModifiedData,
      hasModifiedData,
      legalInfoFormData,
      legalInfoFormModifiedData,
    ]
  )

  return {
    isPartnerInfoFormDataValid,
    validatePartnerData,
    basicInfoFormData,
    legalInfoFormData,
    additionalInfoFormData,
    attachmentsFormData,
    settingsFormData,
    isBasicInfoFormDataValid,
    isLegalInfoFormDataValid,
    isAdditionalInfoFormDataValid,
    isAttachmentsFormDataValid,
    basicInfoFormModifiedData,
    legalInfoFormModifiedData,
    additionalInfoFormModifiedData,
    attachmentsFormModifiedData,
    hasModifiedData,
    hasBasicInfoFormModifiedData,
    hasLegalInfoFormModifiedData,
    hasAdditionalInfoFormModifiedData,
    hasAttachmentsFormModifiedData,
    updateBasicInfoFormData,
    updateLegalInfoFormData,
    updateAdditionalInfoFormData,
    updateAttachmentsFormData,
    updateSettingsFormData,
    resetPartnerInfoFormData,
    formActualPartnerData,
  }
}
